import { useMemo, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Button } from '@mesh.ai/rabbit';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Employee } from 'screens/admin/Reviews/ReviewProcessV1/Hooks/dataHooks/validationDataHooks';
import { SkeletonLoaderReviewCreation } from 'screens/admin/Reviews/ReviewProcessV1/components/SkeletonLoaderReviewCreation';
import { calculateDisplayEmail } from 'utils/helpers/netchexHelpers';
import { addPluralSuffixS } from 'utils/helpers/stringHelpers';

import { Download } from 'assets/assetsV1/iconComponents';

import { AgGridHeader } from 'uiToolkitV1/AgGridHeader';
import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';
import { Avatar } from 'uiToolkitV1/Avatar';
import { ExpandablePaperBody, ExpandablePaperHeader1 } from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const ROW_HEIGHT = 48;
const ROW_HEADER_HEIGHT = 40;

export const EmployeeRenderer: React.FC<ICellRendererParams<Employee>> = ({ data }) => (
  <ListItemComp
    title={
      <Tooltip label={data?.displayName ?? ''}>
        <Box>
          <TextBodyMedium>{data?.displayName ?? ''}</TextBodyMedium>
        </Box>
      </Tooltip>
    }
    isBoldText={false}
    // subTitle={data.email}
    LeftComponent={() => <Avatar src={data.profilePhoto} name={data.displayName} />}
    RightComponent={null}
    subSubtitle={null}
    onClick={() => {}}
    isDisabled={false}
    active={false}
    withoutBackground
  />
);

export const TextRendererDepartment = ({ data }) => (
  <Tooltip label={data?.employeeFunction ?? ''}>
    <Box>
      <TextBodyMedium>{data?.employeeFunction ?? ''}</TextBodyMedium>
    </Box>
  </Tooltip>
);
export const TextRendererDesignation = ({ data }) => (
  <Tooltip label={data?.designation ?? ''}>
    <Box>
      <TextBodyMedium>{data?.designation ?? ''}</TextBodyMedium>
    </Box>
  </Tooltip>
);
export const TextRendererEmail = ({ data }) => (
  <Tooltip label={calculateDisplayEmail(data) ?? ''}>
    <Box>
      <TextBodyMedium>{calculateDisplayEmail(data) ?? ''}</TextBodyMedium>
    </Box>
  </Tooltip>
);

export const TextRendererEmployeeCode = ({ data }) => (
  <Tooltip label={data?.employeeCode ?? ''}>
    <Box>
      <TextBodyMedium>{data?.employeeCode ?? ''}</TextBodyMedium>
    </Box>
  </Tooltip>
);

export const TextRendererCompanyName = ({ data }) => (
  <Tooltip label={data?.companyName ?? ''}>
    <Box>
      <TextBodyMedium>{data?.companyName ?? ''}</TextBodyMedium>
    </Box>
  </Tooltip>
);

export const TextRendererLocation = ({ data }) => (
  <Tooltip label={data?.location ?? ''}>
    <Box>
      <TextBodyMedium>{data?.location ?? ''}</TextBodyMedium>
    </Box>
  </Tooltip>
);

const ValidationTable = ({ rowData, isLoading, title, subTitle }) => {
  // const { reviewCycleId } = useReviewCreationV1Data();
  const gridRef = useRef<AgGridReact>();

  const gridStyle = useMemo(
    () => ({
      height: `calc(${ROW_HEIGHT + 1}px * 6 + ${ROW_HEADER_HEIGHT}px + 24px)`,
      width: '100%',
    }),
    [rowData.length]
  );

  const columnDefs = useMemo<ColDef<Employee>[]>(
    () => [
      {
        headerName: 'Employee',
        cellRenderer: EmployeeRenderer,
        valueGetter: ({ data }) => data.displayName,
        flex: 1.3,
      },
      {
        headerName: 'Employee Code',
        cellRenderer: TextRendererEmployeeCode,
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => data.employeeCode,
        hide: true,
      },
      {
        headerName: 'Department',
        cellRenderer: TextRendererDepartment,
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => data.employeeFunction,
      },
      {
        headerName: 'Designation',
        cellRenderer: TextRendererDesignation,
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => data.designation,
      },
      {
        headerName: 'Email',
        cellRenderer: TextRendererEmail,
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => calculateDisplayEmail(data),
        flex: 1.5,
      },
      {
        headerName: 'Company Name',
        cellRenderer: TextRendererCompanyName,
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => data.companyName,
        hide: true,
      },

      {
        headerName: 'Location',
        cellRenderer: TextRendererLocation,
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => data.location,
        hide: true,
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      headerComponent: AgGridHeader,
      headerComponentParams: {
        enableSorting: true,
        enableMenu: true,
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    }),
    []
  );

  if (isLoading) {
    return (
      <Box mb={3}>
        <SkeletonLoaderReviewCreation />
      </Box>
    );
  }

  // don't show this section if no pending validations
  if (rowData.length === 0) {
    return null;
  }

  const gridStatePersistenceKey = null;

  const downloadCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `${title.toLowerCase().replace(' ', '_')}.csv`,
        allColumns: true,
      });
    }
  };

  return (
    <Flex borderRadius="8px" flexDir="column" border="4px solid #E7EBEF" bg="white" p={3} pb={0}>
      <ExpandablePaperHeader1
        title={title}
        subTitle={subTitle}
        rightComponent={
          <Flex justifyContent="flex-end" alignItems="center">
            <TextLabel bold color="functional.C62C02" mr={3}>
              {rowData.length} Employee{addPluralSuffixS(rowData?.length ?? 0)}
            </TextLabel>
            <Button variant="text" aria-label="download" style={{ padding: '0 8px' }} onClick={downloadCSV}>
              <Download color="#0D819F" boxSize="24px" />
            </Button>
          </Flex>
        }
      />
      <ExpandablePaperBody>
        <div style={gridStyle} className="ag-theme-alpine">
          <AgGridWrapper
            gridStatePersistenceKey={gridStatePersistenceKey}
            useFilterModelPersistence={false}
            useColumnStatePersistence={false}
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowHeight={ROW_HEIGHT}
            headerHeight={ROW_HEADER_HEIGHT}
          />
        </div>
      </ExpandablePaperBody>
    </Flex>
  );
};

export { ValidationTable };
